/**
 * Check if mParticle is active
 *
 * @returns {boolean} true if mParticle is active
 */
function isActive() {
  return !!(
    typeof window !== 'undefined' &&
    window.mParticle &&
    window.mParticle.logEvent &&
    window.mParticle.EventType
  );
}

/**
 * Track page view
 *
 * @returns {Promise<Object>} execution result
 */
function trackPageView() {
  try {
    window.mParticle.logPageView();
    return Promise.resolve({ done: true });
  } catch (err) {
    return Promise.reject(
      new Error(
        `mParticle: Could not execute mParticle.logPageView. Got error: ${err}.`
      )
    );
  }
}

/**
 * Add common context information for tracking
 *
 * @param {Object} attributes event attributes
 * @returns {Object} extended attributes
 */
function addContextData(attributes) {
  return Object.assign(
    {},
    {
      request_referrer: document.referrer,
      request_host: window.location.host,
      request_pathname: window.location.pathname,
      request_search: window.location.search,
    },
    attributes
  );
}

/**
 * Track event corresponding to `type` and `options`
 *
 * @param {Object} options values to track
 * @param {string} type mParticle EventType identifier
 * @returns {Promise<Object>} execution result
 */
function trackEvent(options, type) {
  const { name, attributes, customLabels } = options;
  if (!name) {
    return Promise.reject(new Error('mParticle: Expected name to be defined.'));
  }

  if (!type) {
    return Promise.reject(
      new Error(
        'mParticle: Expected type to be defined in mParticle.EventType.'
      )
    );
  }

  try {
    const attributesWithExtras = addContextData(attributes);

    window.mParticle.logEvent(name, type, attributesWithExtras, customLabels);
    return Promise.resolve({ done: true });
  } catch (err) {
    return Promise.reject(
      new Error(
        `mParticle: Could not execute mParticle.logEvent. Got error: ${err}.`
      )
    );
  }
}

/**
 * Track navigation event corresponding `options`
 *
 * @param {Object} options values to track
 * @returns {Promise<Object>} execution result
 */
function trackNavigation(options) {
  const mParticleType = window.mParticle.EventType.Navigation;
  return trackEvent(options, mParticleType);
}

/**
 * Track social event corresponding `options`
 *
 * @param {Object} options values to track
 * @returns {Promise<Object>} execution result
 */
function trackSocial(options) {
  const mParticleType = window.mParticle.EventType.Social;
  return trackEvent(options, mParticleType);
}

/**
 * Track transaction event corresponding `options`
 *
 * @param {Object} options values to track
 * @returns {Promise<Object>} execution result
 */
function trackTransaction(options) {
  const mParticleType = window.mParticle.EventType.Transaction;
  return trackEvent(options, mParticleType);
}

/**
 * Track login by delegating to mParticle login
 * A successful login is followed by automatic setting of other attributes
 * provided in `options.attributes`
 *
 * @param {Object} options user attributes for login
 * @returns {Promise<Object>} execution result
 */
function trackLogin(options) {
  const { attributes } = options;
  if (!attributes) {
    return Promise.reject(
      new Error('mParticle: Expected attributes to be defined for login.')
    );
  }

  const { email, id, ...otherAttributes } = attributes;
  if (!id) {
    return Promise.reject(
      new Error('mParticle: Expected id to be defined for login.')
    );
  }

  const userIdentities = {
    customerid: id,
  };

  if (email) {
    userIdentities.email = email;
  }

  const identityRequest = { userIdentities };
  return new Promise((resolve, reject) => {
    try {
      window.mParticle.Identity.login(identityRequest, result => {
        if (result.httpCode !== 200) {
          reject(
            new Error(
              // eslint-disable-next-line max-len
              `mParticle: Unsuccessful response while logging in user: ${id} - ${JSON.stringify(
                attributes
              )}. Got: ${JSON.stringify(result)}.`
            )
          );
          return;
        }

        const user = result.getUser();
        if (!user) {
          reject(
            new Error(
              // eslint-disable-next-line max-len
              `mParticle: No user returned while attempting to logging in user: ${id} - ${JSON.stringify(
                attributes
              )}.`
            )
          );
          return;
        }

        Object.keys(otherAttributes).forEach(attribute => {
          user.setUserAttribute(attribute, otherAttributes[attribute]);
        });

        resolve({ done: true, user });
      });
    } catch (err) {
      reject(
        new Error(
          // eslint-disable-next-line max-len
          `mParticle: Could not execute mParticle.Identity.login. Got error: ${err}`
        )
      );
    }
  });
}

/**
 * Track logout by delegating to mParticle logout
 *
 * @returns {Promise<Object>} execution result
 */
function trackLogout() {
  return new Promise((resolve, reject) => {
    try {
      window.mParticle.Identity.logout({}, result => {
        if (result.httpCode !== 200) {
          reject(
            new Error(
              // eslint-disable-next-line max-len
              `mParticle: Unsuccessful response while logging out user. Got: ${JSON.stringify(
                result
              )}`
            )
          );
          return;
        }

        const user = result.getUser();
        if (!user) {
          reject(
            new Error(
              `mParticle: No user returned while attempting to log out user.`
            )
          );
          return;
        }

        resolve({ done: true, user });
      });
    } catch (err) {
      reject(
        new Error(
          // eslint-disable-next-line max-len
          `mParticle: Could not execute mParticle.Identity.logout. Got error: ${err}`
        )
      );
    }
  });
}

/**
 * Track user attributes by setting them on current mParticle user
 *
 * @param {Object} options object containing user attributes
 * @returns {Promise<Object>} execution result
 */
function trackUserAttributes(options) {
  const { attributes } = options;
  if (!attributes) {
    return Promise.reject(
      new Error(
        'mParticle: Expected attributes to be defined for setUserAttribute.'
      )
    );
  }

  try {
    const user = window.mParticle.Identity.getCurrentUser();
    if (!user) {
      return Promise.reject(
        new Error('mParticle: User is not defined. Attributes cannot be set.')
      );
    }

    Object.keys(attributes).forEach(attribute => {
      user.setUserAttribute(attribute, attributes[attribute]);
    });

    return Promise.resolve({ done: true, user });
  } catch (err) {
    return Promise.reject(
      new Error(
        `mParticle: Could not execute user.setUserAttribute. Got error: ${err}.`
      )
    );
  }
}

module.exports = {
  isActive,
  trackPageView,
  trackNavigation,
  trackSocial,
  trackTransaction,
  trackLogin,
  trackLogout,
  trackUserAttributes,
};
